/**
 * Approval Inbox — Sprint 7' Human-in-the-Loop.
 *
 * Two-pane layout:
 *   - left:  list of approvals (filterable by status), live-updating
 *            on `approval.requested` / `approval.granted` / `approval.denied`
 *            / `approval.executed` events
 *   - right: detail of the selected approval — reasoning, confidence,
 *            impact, rollback plan, timeline, action buttons
 *
 * Design choices:
 *   - Live refresh: when a relevant event arrives, we re-fetch the list
 *     (cheap) and, if the detail pane is showing that approval, re-fetch
 *     its detail too. Avoids stitching state from event payloads.
 *   - No optimistic UI on approve/reject — the server is the source of
 *     truth; a 409 from a stale UI is a real signal that another operator
 *     already decided, and we surface it as an error.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Check, Clock, FileText, Inbox as InboxIcon, ShieldAlert, X,
} from "lucide-react";
import { Badge, Button, Card, CardHeader, cn } from "@vseh/ui";

import {
  approvalsApi,
  type ApprovalDto,
  type ApprovalDetailDto,
} from "../shared/api";
import { useEventStream, type EventEnvelope } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { ErrorBlock, LoadingBlock } from "../shared/components/States";


type StatusFilter = "all" | ApprovalDto["status"];

// Keys (not labels). Translated by `useT()` inside the component
// at render time so a language toggle updates the pills without
// a reload.
const STATUS_FILTERS: { key: StatusFilter; labelKey: string }[] = [
  { key: "pending",   labelKey: "inbox.filter.pending"  },
  { key: "approved",  labelKey: "inbox.filter.approved" },
  { key: "executed",  labelKey: "inbox.filter.executed" },
  { key: "rejected",  labelKey: "inbox.filter.rejected" },
  { key: "all",       labelKey: "inbox.filter.all"      },
];


export function InboxPage(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<StatusFilter>("pending");
  const [items, setItems] = useState<ApprovalDto[]>([]);
  const [listError, setListError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApprovalDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [decisionInFlight, setDecisionInFlight] = useState(false);

  // ---- list fetcher (re-runs on filter change and on relevant events) ----
  const refreshList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const r = await approvalsApi.list(
        filter === "all" ? { limit: 100 } : { status: filter, limit: 100 },
      );
      setItems(r);
    } catch (e) {
      setListError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void refreshList(); }, [refreshList]);

  // ---- detail fetcher (when an approval is selected) ---------------------
  const refreshDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const d = await approvalsApi.get(id);
      setDetail(d);
      // Fire-and-forget view ping. The server stamps viewed_at on first
      // call and is idempotent on subsequent calls.
      void approvalsApi.view(id).catch(() => {});
    } catch (e) {
      setListError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) void refreshDetail(selectedId);
    else setDetail(null);
  }, [selectedId, refreshDetail]);

  // ---- live updates ------------------------------------------------------
  const onLiveEvent = useCallback((ev: EventEnvelope) => {
    if (!ev.event_type?.startsWith("approval.")) return;
    void refreshList();
    const payload = ev.payload as { approval_id?: string } | undefined;
    if (selectedId && payload?.approval_id === selectedId) {
      void refreshDetail(selectedId);
    }
  }, [refreshList, refreshDetail, selectedId]);

  useEventStream(onLiveEvent, { subjects: ["approval.>"] });

  // ---- decision actions --------------------------------------------------
  // Sprint AC — approve may carry a `modified_payload` from the reviewer's
  // edits to the agent's proposal. Reject keeps to note-only (rejecting
  // a payload edit doesn't make semantic sense — we don't execute it).
  const onApprove = useCallback(async (
    note?: string,
    modifiedPayload?: Record<string, unknown>,
  ) => {
    if (!selectedId) return;
    setDecisionInFlight(true);
    try {
      const body: Record<string, unknown> = {};
      if (note) body.note = note;
      if (modifiedPayload !== undefined) body.modified_payload = modifiedPayload;
      await approvalsApi.approve(selectedId, body);
      await refreshList();
      await refreshDetail(selectedId);
    } catch (e) {
      setListError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setDecisionInFlight(false);
    }
  }, [selectedId, refreshList, refreshDetail]);

  const onReject = useCallback(async (note?: string) => {
    if (!selectedId) return;
    setDecisionInFlight(true);
    try {
      await approvalsApi.reject(selectedId, note ? { note } : {});
      await refreshList();
      await refreshDetail(selectedId);
    } catch (e) {
      setListError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setDecisionInFlight(false);
    }
  }, [selectedId, refreshList, refreshDetail]);

  // ---- render ------------------------------------------------------------
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-5">
        <Card>
          <CardHeader
            title={t("inbox.title")}
            description={t("inbox.subtitle")}
            actions={
              <span className="text-2xs text-base-500 tabular-nums">
                {items.length}
              </span>
            }
          />
          <div className="mb-3 flex flex-wrap gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={cn(
                  "rounded-sm px-2 py-1 text-2xs font-medium",
                  filter === f.key
                    ? "bg-primary-900 text-white"
                    : "bg-base-100 text-base-700 hover:bg-base-200",
                )}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>

          {loading && items.length === 0 && <LoadingBlock />}
          {listError && <ErrorBlock error={listError} onRetry={refreshList} />}

          {items.length === 0 && !loading && !listError && (
            <div className="grid place-items-center gap-2 py-10 text-center text-sm text-base-500">
              <InboxIcon aria-hidden="true" className="size-8 text-base-300" />
              <span>{t("inbox.empty")}</span>
            </div>
          )}

          <ul className="space-y-1.5">
            {items.map((a) => (
              <ApprovalRow
                key={a.id}
                approval={a}
                active={a.id === selectedId}
                onSelect={() => setSelectedId(a.id)}
              />
            ))}
          </ul>
        </Card>
      </div>

      <div className="col-span-7">
        {!selectedId && (
          <Card>
            <div className="grid place-items-center gap-2 py-12 text-center text-sm text-base-500">
              <FileText aria-hidden="true" className="size-8 text-base-300" />
              {t("inbox.placeholder.select")}
            </div>
          </Card>
        )}
        {selectedId && (
          <ApprovalDetailView
            detail={detail}
            loading={detailLoading}
            decisionInFlight={decisionInFlight}
            onApprove={onApprove}
            onReject={onReject}
          />
        )}
      </div>
    </div>
  );
}


// =============================================================================
// Row + detail components
// =============================================================================


function ApprovalRow(props: {
  approval: ApprovalDto;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  const t = useT();
  const a = props.approval;
  return (
    <li>
      <button
        type="button"
        onClick={props.onSelect}
        className={cn(
          "w-full rounded-md border px-3 py-2 text-left transition-colors",
          props.active
            ? "border-ai-500 bg-ai-50"
            : "border-base-200 bg-surface hover:bg-base-50",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-mono text-xs text-base-800">
            {a.action}
          </span>
          <div className="flex items-center gap-1">
            {a.escalation_count > 0 && (
              <Badge tone="danger" dot>{t("inbox.badge.escalation")}</Badge>
            )}
            <StatusBadge status={a.status} />
          </div>
        </div>
        <div className="mt-1 line-clamp-2 text-sm text-base-700">
          {a.reasoning}
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-2xs text-base-500">
          <span className="font-mono">{a.proposed_by_agent}</span>
          <ConfidencePill value={a.confidence} />
          <span className="tabular-nums">
            {new Date(a.created_at).toLocaleTimeString("ru-RU")}
          </span>
        </div>
      </button>
    </li>
  );
}


function ApprovalDetailView(props: {
  detail: ApprovalDetailDto | null;
  loading: boolean;
  decisionInFlight: boolean;
  onApprove: (
    note?: string,
    modifiedPayload?: Record<string, unknown>,
  ) => void;
  onReject: (note?: string) => void;
}): JSX.Element {
  const t = useT();
  const [note, setNote] = useState("");
  const d = props.detail;

  // Sprint AC — payload editor. Pre-populated from the agent's
  // proposal; the reviewer can tweak fields before approving. We
  // parse on submit and short-circuit the approve if the JSON
  // doesn't round-trip. Re-keying on `d?.id` so switching approvals
  // resets the editor rather than carrying state across rows.
  const initialPayload = useMemo(
    () => JSON.stringify(d?.payload ?? {}, null, 2),
    [d?.id],                                        // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [editorText, setEditorText] = useState(initialPayload);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  // Reset the editor whenever the user picks a different approval.
  useEffect(() => {
    setEditorText(initialPayload);
    setEditorOpen(false);
    setEditorError(null);
  }, [initialPayload]);

  const isEdited = editorText.trim() !== initialPayload.trim();

  const canDecide = useMemo(
    () => d?.status === "pending",
    [d?.status],
  );

  if (props.loading && !d) return <Card><LoadingBlock /></Card>;
  if (!d) return <Card>—</Card>;

  return (
    <Card>
      <CardHeader
        title={<span className="font-mono text-sm">{d.action}</span>}
        description={
          <span className="text-2xs text-base-500">
            {t("inbox.proposed_by", { agent: d.proposed_by_agent })}{" "}
            {new Date(d.created_at).toLocaleString("ru-RU")}
          </span>
        }
        actions={<StatusBadge status={d.status} />}
      />

      <div className="space-y-4">
        <SectionLabel icon={<FileText className="size-3.5" />} text={t("inbox.section.reasoning")} />
        <p className="text-sm text-base-800">{d.reasoning}</p>

        <div className="flex flex-wrap items-center gap-2">
          <ConfidencePill value={d.confidence} />
          <ExpiryPill expiresAt={d.expires_at} status={d.status} />
          {d.chain_id && <ChainPill approval={d} />}
        </div>

        {Object.keys(d.impact_estimate).length > 0 && (
          <div>
            <SectionLabel icon={<AlertTriangle className="size-3.5" />} text={t("inbox.section.changes")} />
            <pre className="mt-1 overflow-x-auto rounded-sm bg-base-50 p-2 text-2xs text-base-700">
              {JSON.stringify(d.impact_estimate, null, 2)}
            </pre>
          </div>
        )}

        {d.rollback_plan && (
          <div>
            <SectionLabel icon={<ShieldAlert className="size-3.5" />} text={t("inbox.section.rollback")} />
            <p className="mt-1 text-xs text-base-700">{d.rollback_plan}</p>
          </div>
        )}

        <div>
          <SectionLabel icon={<Clock className="size-3.5" />} text="Timeline" />
          <ol className="mt-2 space-y-1.5">
            {d.events.map((e) => (
              <li key={e.id} className="flex items-start gap-2 text-xs">
                <span className="mt-1 size-1.5 shrink-0 rounded-full bg-ai-500" aria-hidden="true" />
                <div className="flex-1">
                  <span className="font-mono text-base-800">{e.event_type}</span>
                  {e.actor_id && (
                    <span className="ml-2 text-2xs text-base-500">
                      {e.actor_type ?? "?"}/{e.actor_id.slice(0, 8)}
                    </span>
                  )}
                  {e.note && <div className="text-base-600">{e.note}</div>}
                </div>
                <span className="text-2xs text-base-500 tabular-nums">
                  {new Date(e.occurred_at).toLocaleTimeString("ru-RU")}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {canDecide && (
          <div className="border-t border-base-200 pt-4">
            <SectionLabel icon={<Check className="size-3.5" />} text={t("inbox.section.decision")} />
            <textarea
              className="mt-2 w-full rounded-sm border border-base-300 px-3 py-2 text-sm"
              rows={2}
              placeholder={t("inbox.comment.placeholder")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            {/* Sprint AC — payload editor. Collapsed by default to keep
                the common "approve as-is" flow one click. When expanded,
                the reviewer edits a pretty-printed JSON copy of the
                proposal; on submit we parse, validate, and pass it as
                `modified_payload` so the agent executes the edited form. */}
            <div className="mt-2 rounded-sm border border-base-200 bg-base-50">
              <button
                type="button"
                onClick={() => setEditorOpen((v) => !v)}
                className="flex w-full items-center justify-between px-3 py-1.5 text-2xs font-medium uppercase tracking-wide text-base-600 hover:bg-base-100"
              >
                <span className="flex items-center gap-1.5">
                  <FileText className="size-3.5" />
                  {t("inbox.editor.toggle")}
                  {isEdited && (
                    <Badge tone="ai">{t("inbox.badge.modified")}</Badge>
                  )}
                </span>
                <span className="font-mono text-2xs">{editorOpen ? "−" : "+"}</span>
              </button>
              {editorOpen && (
                <div className="border-t border-base-200 p-2">
                  <textarea
                    className="w-full rounded-sm border border-base-300 bg-surface px-2 py-1.5 font-mono text-2xs"
                    rows={Math.min(12, Math.max(4, editorText.split("\n").length + 1))}
                    spellCheck={false}
                    value={editorText}
                    onChange={(e) => {
                      setEditorText(e.target.value);
                      setEditorError(null);
                    }}
                  />
                  <div className="mt-1 flex items-center justify-between gap-2">
                    {editorError ? (
                      <span className="text-2xs text-danger-700">{editorError}</span>
                    ) : (
                      <span className="text-2xs text-base-500">
                        {t("inbox.editor.hint")}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditorText(initialPayload);
                        setEditorError(null);
                      }}
                      className="text-2xs text-base-600 underline hover:text-base-900"
                    >
                      {t("inbox.editor.reset")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2 flex gap-2">
              <Button
                variant="ai"
                leftIcon={<Check className="size-4" />}
                loading={props.decisionInFlight}
                onClick={() => {
                  // Approve as-is if the editor wasn't touched; otherwise
                  // parse and pass modified_payload. A parse error blocks
                  // the call so the agent never sees malformed JSON.
                  if (!isEdited) {
                    props.onApprove(note.trim() || undefined);
                    return;
                  }
                  let parsed: Record<string, unknown>;
                  try {
                    const candidate = JSON.parse(editorText);
                    if (candidate === null || typeof candidate !== "object" || Array.isArray(candidate)) {
                      setEditorError(t("inbox.editor.error.object"));
                      setEditorOpen(true);
                      return;
                    }
                    parsed = candidate as Record<string, unknown>;
                  } catch (err) {
                    setEditorError(
                      err instanceof Error ? err.message : t("inbox.editor.error.invalid"),
                    );
                    setEditorOpen(true);
                    return;
                  }
                  props.onApprove(note.trim() || undefined, parsed);
                }}
              >
                {isEdited ? t("inbox.action.approve_modified") : t("inbox.action.approve")}
              </Button>
              <Button
                variant="ghost"
                leftIcon={<X className="size-4" />}
                disabled={props.decisionInFlight}
                onClick={() => props.onReject(note.trim() || undefined)}
              >
                {t("inbox.action.reject")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}


// =============================================================================
// Tiny presentation helpers
// =============================================================================


function StatusBadge({ status }: { status: ApprovalDto["status"] }): JSX.Element {
  const tone = {
    pending:     "ai"      as const,
    approved:    "success" as const,
    executed:    "success" as const,
    rejected:    "danger"  as const,
    failed:      "danger"  as const,
    expired:     "neutral" as const,
    rolled_back: "neutral" as const,
    cancelled:   "neutral" as const,
  }[status];
  return <Badge tone={tone} dot>{status}</Badge>;
}


function ConfidencePill({ value }: { value: number }): JSX.Element {
  const pct = Math.round(value * 100);
  const tone = value >= 0.8 ? "success" : value >= 0.5 ? "ai" : "danger";
  return (
    <Badge tone={tone}>
      <span className="tabular-nums">{pct}%</span>
    </Badge>
  );
}


function ExpiryPill({
  expiresAt,
  status,
}: {
  expiresAt: string;
  status: ApprovalDto["status"];
}): JSX.Element | null {
  const t = useT();
  if (status !== "pending") return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms < 0) return <Badge tone="danger">{t("inbox.badge.expired")}</Badge>;
  const hours = Math.round(ms / 3_600_000);
  return (
    <Badge tone="neutral">
      <span className="tabular-nums">{t("inbox.expires_in", { hours })}</span>
    </Badge>
  );
}


function ChainPill({ approval }: { approval: ApprovalDto }): JSX.Element {
  const t = useT();
  // Show "Step N · awaits previous" when this row can't be decided yet,
  // "Step N · ready" when its predecessor is approved (or there is no
  // predecessor), or just "Step N" when the row itself isn't pending.
  // The exact predecessor lookup happens server-side at decision time;
  // the UI is a hint, not a gate.
  const label = t("inbox.chain.step", { n: approval.chain_order ?? "?" });
  if (approval.status !== "pending") {
    return <Badge tone="neutral">{label}</Badge>;
  }
  if (approval.requires_after) {
    return (
      <Badge tone="heritage">
        {t("inbox.chain.waiting", { label })}
      </Badge>
    );
  }
  return <Badge tone="ai">{t("inbox.chain.ready", { label })}</Badge>;
}


function SectionLabel({ icon, text }: { icon: JSX.Element; text: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-base-500">
      {icon}
      {text}
    </div>
  );
}
